import React, { useState, useRef } from 'react';
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Typography, CircularProgress } from "@mui/material"; // ✅ Restored
import Card from "@mui/material/Card"; // ✅ Restored
import EntitiesPopup from "layouts/components/EntitiesPopup"; // ✅ Restored
import { generateColors } from "utils/chartColors"; // ✅ Import color generator
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded"; // ✅ Import Facebook Icon


// -----------------------------------------------------------
// 🚀 HELPER FUNCTION
// -----------------------------------------------------------

/**
 * Extracts the daily impression/view count based on platform logic
 * derived from platformUtils.
 */
const getImpressionValue = (item, platform) => {
    if (!item) return 0;

    // Facebook: Page Impressions (preferred) or sum of post views
    if (platform === "Facebook") {
        return item.pageMetrics?.page_impressions || item.pageMetrics?.page_media_view || 0;
    }

    // YouTube: Channel Views (preferred)
    if (platform === "YouTube") {
        // Preferred: Direct channel metrics
        if (item.channelMetrics?.views || item.channelMetrics?.impressions) {
            return item.channelMetrics?.views || item.channelMetrics?.impressions || 0;
        }

        // Fallback: Sum of Video Views
        if (item.videos && Array.isArray(item.videos)) {
            return item.videos.reduce((sum, video) => {
                return sum + (video.videoMetrics?.viewCount || 0) + (video.postMetrics?.views || 0);
            }, 0);
        }
        return 0;
    }

    // Instagram: Profile Impressions (preferred)
    if (platform === "Instagram") {
        // Preferred: Direct profile metrics
        if (item.profileMetrics?.impressions || item.profileMetrics?.views) {
            return item.profileMetrics?.impressions || item.profileMetrics?.views || 0;
        }

        // Fallback: Sum of Post Views
        if (item.posts && Array.isArray(item.posts)) {
            return item.posts.reduce((sum, post) => {
                return sum + (post.postMetrics?.views || 0);
            }, 0);
        }
        return 0;
    }

    return 0;
};


// -----------------------------------------------------------
// 🚀 MAIN COMPONENT
// -----------------------------------------------------------

function AudienceViewChart({
    title,
    datasets,
    previousdatasets = [],
    futuredatasets = [],
    isPredictionEnabled = false,
    isLoading = false,
    isEntitled = true,   // ✅ Added
    featureName = "",    // ✅ Added
    planRequired = "",   // ✅ Added
    entitlements, // ✅ Added prop
    viewMode, // ✅ NEW
    onViewModeChange, // ✅ NEW
    profileColorMap // ✅ Global Color Map
}) {
    // ✅ State for the popup
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [popupDate, setPopupDate] = useState(null);

    // ✅ Click handlers for the popup
    const handleBarClick = (date) => {
        setPopupDate(date);
        setIsPopupOpen(true);
    };

    const handlePopupClose = () => {
        setIsPopupOpen(false);
        setPopupDate(null);
    };

    // ---------------------------------------------------------------------
    // RENDER LOADER
    // ---------------------------------------------------------------------
    const renderLoader = () => (
        <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
                borderRadius: "0.75rem",
            }}
        >
            <CircularProgress size={32} thickness={4} color="accent" />
        </MDBox>
    );

    // Use provided title or derive one? The parent usually passes a good title.
    // We'll trust the parent's title for now or fallback.
    const chartTitle = title || "Views Over Time";

    // Safely fallback
    const safeDatasets = datasets || [];
    const safePreviousDatasets = previousdatasets || [];
    const safeFutureDatasets = futuredatasets || [];

    // ✅ Use provided Future Data if prediction is enabled
    let futureData = isPredictionEnabled && safeFutureDatasets ? safeFutureDatasets : [];

    // ✅ Align labels
    const actualLabels = safeDatasets.length > 0 ? safeDatasets[0].data.map((item) => item.date) : [];
    const predictedLabels = futureData.length > 0 && safeDatasets.length > 0
        ? futureData[0].data.slice(safeDatasets[0].data.length).map((item) => item.date)
        : [];
    const chartLabels = [...actualLabels, ...predictedLabels];

    // ⬇️=========================================
    // ⬇️ CALCULATION HELPER
    // ⬇️=========================================
    const extractDailyImpressions = (profile) => {
        const data = [];

        profile.data.forEach((item) => {
            const val = getImpressionValue(item, profile.platform);
            data.push(val);
        });

        return data;
    };

    // 🚀 CHECK IF ELIGIBLE FOR GRANULAR BREAKDOWN
    // Condition: All profiles must be Facebook AND Owned
    const isFacebookGranular = safeDatasets.length > 0 && safeDatasets.every(p =>
        p.platform === 'Facebook' && p.own === true
    );

    const generatedColors = generateColors(safeDatasets.length, safeDatasets.map(d => d.profileId));

    // Helper to add opacity (Copied from AudienceGrowthChart)
    const hexToRgba = (hex, alpha) => {
        let r = 0, g = 0, b = 0;
        if (hex.startsWith('#')) hex = hex.slice(1);
        if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        if (hex.length === 6) {
            r = parseInt(hex.slice(0, 2), 16);
            g = parseInt(hex.slice(2, 4), 16);
            b = parseInt(hex.slice(4, 6), 16);
        }
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // ✅ Construct Chart Data
    let processedDatasets = [];

    if (isFacebookGranular) {
        // 🔹 GRANULAR MODE (Stacked Organic vs Paid)
        safeDatasets.forEach((profile, index) => {
            const organicData = [];
            const paidData = [];

            // Base Color
            const baseColorHex = profileColorMap[profile.profileId] || generatedColors[index] || "#3b5998";
            const COLOR_ORGANIC = hexToRgba(baseColorHex, 0.9);
            const COLOR_PAID = hexToRgba(baseColorHex, 0.4);

            profile.data.forEach(item => {
                const paid = item.pageMetrics?.page_media_view_paid || 0;
                const organic = item.pageMetrics?.page_media_view_organic || 0;
                paidData.push(paid);
                organicData.push(organic);
            });

            const stackId = `stack-${profile.profileId}`;

            // 1. Organic (Base)
            processedDatasets.push({
                label: profile.label || profile.name,
                data: organicData,
                backgroundColor: COLOR_ORGANIC,
                borderColor: baseColorHex,
                borderWidth: 1,
                stack: stackId,
                profileId: profile.profileId,
                profilePictureUrl: profile.profilePictureUrl,
                platform: profile.platform,
                metricName: "Organic Views"
            });

            // 2. Paid (Top)
            processedDatasets.push({
                label: profile.label || profile.name,
                data: paidData,
                backgroundColor: COLOR_PAID,
                borderColor: baseColorHex,
                borderWidth: 1,
                stack: stackId,
                profileId: profile.profileId,
                profilePictureUrl: profile.profilePictureUrl,
                platform: profile.platform,
                metricName: "Paid Views",
                skipLegend: true // Hint for custom legend logic if we used it elsewhere
            });
        });
    } else {
        // 🔹 STANDARD MODE (Total Views)
        processedDatasets = safeDatasets.map((profile) => {
            const data = extractDailyImpressions(profile);
            return {
                label: profile.name || profile.label,
                data: data,
                profileId: profile.profileId,
                profilePictureUrl: profile.profilePictureUrl || '',
                platform: profile.platform || "Unknown",
                metricName: "Views",
            };
        });
    }

    const computedChartData = {
        labels: chartLabels,
        datasets: processedDatasets,
        // 🔹 Predicted Data
        ...futureData.map((profile) => {
            const data = extractDailyImpressions(profile);
            return {
                label: `${profile.name || profile.label} (Predicted)`,
                data: data,
                profileId: profile.profileId,
                profilePictureUrl: profile.profilePictureUrl || '',
                platform: profile.platform || "Unknown",
                metricName: "Predicted Views",
            };
        }),
        // 🔹 Previous Data
        previousdatasets: safePreviousDatasets.map((profile) => {
            const data = extractDailyImpressions(profile);
            return {
                label: profile.name || profile.label,
                data: data,
                profileId: profile.profileId,
                profilePictureUrl: profile.profilePictureUrl || '',
                platform: profile.platform || "Unknown",
                metricName: "Views",
            };
        }),
    };

    // 🚀 CUSTOM LEGEND (Deduplicated)
    const renderCustomLegend = () => {
        if (!hasData) return null;
        return (
            <MDBox display="flex" justifyContent="center" alignItems="center" flexWrap="wrap" px={2} py={1}>
                {safeDatasets.map((profile, i) => {
                    const baseColor = profileColorMap[profile.profileId] || generatedColors[i] || "#3b5998";
                    return (
                        <MDBox key={i} display="flex" alignItems="center" mr={2} mb={1}>
                            {/* Rectangular color box */}
                            <MDBox
                                sx={{
                                    width: 20,
                                    height: 10,
                                    borderRadius: "2px",
                                    backgroundColor: baseColor,
                                    mr: 1,
                                }}
                            />
                            {/* Platform Icon (Always Facebook in this mode) */}
                            <FacebookRoundedIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />

                            <MDTypography variant="button" color="text" sx={{ fontWeight: 300, ml: 0.5 }}>
                                {profile.name || profile.label || "Unknown Profile"}
                            </MDTypography>
                        </MDBox>
                    );
                })}
            </MDBox>
        );
    };

    // 🚀 CUSTOM METRICS (Aggregated Total for Stacked Mode)
    const customMetrics = isFacebookGranular ? safeDatasets.map((profile) => {
        const organicTotal = profile.data.reduce((sum, item) => sum + (item.pageMetrics?.page_media_view_organic || 0), 0);
        const paidTotal = profile.data.reduce((sum, item) => sum + (item.pageMetrics?.page_media_view_paid || 0), 0);
        const total = organicTotal + paidTotal;

        // Previous Data (Total)
        const prevProfile = safePreviousDatasets.find(p => p.profileId === profile.profileId);
        const prevTotal = prevProfile ? extractDailyImpressions(prevProfile).reduce((a, b) => a + b, 0) : 0;

        return {
            profilePic: profile.profilePictureUrl || "https://via.placeholder.com/150",
            name: profile.name || profile.label || "Unknown Profile", // ✅ Fixed: Fallback to label
            platform: profile.platform || "Unknown Platform",
            metricName: "Total Views",
            metricValue: total.toLocaleString(),
            previousMetricValue: prevTotal.toLocaleString(),
            isQualitative: false,
        };
    }) : null;

    // 🚀 OVERRIDE OPTIONS (Tooltip for Stacked Mode)
    const overrideOptions = isFacebookGranular ? {
        interaction: {
            mode: 'index', // ✅ Show all datasets at this index (Organic + Paid)
            intersect: false,
        },
        plugins: {
            tooltip: {
                callbacks: {
                    label: function (context) {
                        // ✅ Use metricName ("Organic Views" / "Paid Views") instead of Profile Name
                        let label = context.dataset.metricName || context.dataset.label || '';
                        let value = context.parsed.y;
                        return `${label}: ${Number(value).toLocaleString()}`;
                    },
                    footer: (tooltipItems) => {
                        let total = 0;
                        tooltipItems.forEach(function (tooltipItem) {
                            total += tooltipItem.parsed.y;
                        });
                        return 'Total: ' + total.toLocaleString();
                    }
                }
            }
        }
    } : null;

    // -----------------------------------------------------------
    // PERSISTENCE LOGIC
    // -----------------------------------------------------------
    const lastChartDataRef = useRef(null);

    if (safeDatasets.length > 0) {
        lastChartDataRef.current = computedChartData;
    }

    let dataToRender = computedChartData;
    if (isLoading && safeDatasets.length === 0 && lastChartDataRef.current) {
        dataToRender = lastChartDataRef.current;
    }

    const hasData = (dataToRender.datasets && dataToRender.datasets.length > 0);

    // ✅ Tooltip
    const tooltipContent = (
        <div style={{ maxWidth: "350px", textAlign: "left" }}>
            <Typography variant="body2" gutterBottom>
                <strong>Description:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                {isFacebookGranular
                    ? "This chart displays the daily Views, broken down by Organic (free) and Paid (ads) sources."
                    : "This chart displays the total number of Views generated by your content daily."
                }
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>How to read the chart:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                {isFacebookGranular ? (
                    <>
                        - <strong>Organic (Darker):</strong> Views gained naturally without ad spend.<br />
                        - <strong>Paid (Lighter):</strong> Views gained specifically from ads.<br />
                        - <strong>Total Height:</strong> Represents the total views for the day.
                    </>
                ) : (
                    <>
                        - <strong>High Peaks</strong>: Indicates viral content or successful campaigns. <br />
                        - <strong>Consistent Levels</strong>: Shows steady audience reach. <br />
                    </>
                )}
            </Typography>
        </div>
    );

    return (
        <>
            {isLoading && renderLoader()}

            {
                hasData ? (
                    <VerticalBarChart
                        title={chartTitle}
                        description={tooltipContent}
                        height="19.125rem"
                        chart={dataToRender}
                        aggregate={true}
                        onBarClick={handleBarClick}
                        isEntitled={isEntitled}
                        featureName={featureName}
                        planRequired={planRequired}
                        rawData={datasets}
                        entitlements={entitlements} // ✅ Pass entitlements
                        viewMode={viewMode} // ✅ Pass down CORRECT PROP NAME
                        onViewModeChange={onViewModeChange} // ✅ Pass down
                        stacked={isFacebookGranular} // ✅ Enable Stacking
                        customMetrics={customMetrics} // ✅ Pass Deduplicated Metrics
                        customLegend={renderCustomLegend()} // ✅ Pass Clean Legend
                        overrideOptions={overrideOptions} // ✅ Pass Tooltip Config
                    />
                ) : (
                    <Card sx={{ height: "19.125rem", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        {!isLoading && (
                            <MDTypography variant="body2" color="text">
                                No view data available.
                            </MDTypography>
                        )}
                    </Card>
                )
            }

            {
                isPopupOpen && (
                    <EntitiesPopup
                        isModalOpen={isPopupOpen}
                        onClose={handlePopupClose}
                        datasets={datasets}
                        date={popupDate}
                    />
                )
            }
        </>
    );
}

// 🛡️ PropTypes Validation
AudienceViewChart.propTypes = {
    title: PropTypes.string,
    datasets: PropTypes.array.isRequired,
    previousdatasets: PropTypes.array,
    futuredatasets: PropTypes.array,
    isPredictionEnabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    isEntitled: PropTypes.bool,
    featureName: PropTypes.string,
    planRequired: PropTypes.string,
    entitlements: PropTypes.object, // ✅ Added propType
    viewMode: PropTypes.string,
    onViewModeChange: PropTypes.func,
    profileColorMap: PropTypes.object,
};

export default AudienceViewChart;
